import React, { useEffect, useState, useContext } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { request } from "../../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import { makeError } from "../../../utils";
import * as Yup from "yup";
import NotFoundTitle from "../utilComponent/NotFoundTitle";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaImage } from "react-icons/fa";
import { AuthContext } from "../../../auth/AuthContext";

const AddServices = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataFound, setDataFound] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    user_id: user?.id || "",
    serviceId: "",
    title: "",
    price: "",
    qty: 1,
    description: "",
    timeline: "",
    image: null,
  });

  const schema = Yup.object().shape({
    title: Yup.string().required("Service title is required"),
    price: Yup.number()
      .typeError("Price must be a number")
      .required("Price is required"),
    qty: Yup.number()
      .typeError("Quantity must be a number")
      .min(1, "Quantity must be at least 1"),
    timeline: Yup.string().required("Timeline is required"),
    description: Yup.string().test(
      "is-empty",
      "Service description is required",
      (value) =>
        value && value.replace(/<(.|\n)*?>/g, "").trim().length > 0
    ),
  });

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);

    try {
      await schema.validateAt(name, updated);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [name]: error.message }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFormData((prev) => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };

  const getServiceById = async () => {
    try {
      setLoading(true);
      const res = await request({
        url: `services/${id}`,
        method: "GET",
      });

      if (res?.data) {
        setFormData({
          user_id: res.data.user_id || user?.id || "",
          serviceId: res.data.id || "",
          title: res.data.title || "",
          price: res.data.price || "",
          qty: res.data.qty || 1,
          description: res.data.description || "",
          timeline: res.data.timeline || "",
          image: null,
        });

        setImagePreview(res.data.image_url || null);
        setDataFound(true);
      } else {
        setDataFound(false);
      }
    } catch (error) {
      setDataFound(false);
      makeError(error, toast);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) getServiceById();
  }, [id]);

  const submit = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      id ? updateService() : addService();
    } catch (err) {
      makeError(err, toast);
      const newErrors = {};
      err.inner.forEach((e) => {
        newErrors[e.path] = e.message;
      });
      setErrors(newErrors);
    }
  };

  const getPayload = () => {
    const payload = new FormData();
    payload.append("user_id", formData.user_id);
    payload.append("title", formData.title);
    payload.append("price", formData.price);
    payload.append("qty", formData.qty);
    payload.append("description", formData.description);
    payload.append("timeline", formData.timeline);
    if (formData.image) payload.append("image", formData.image);
    return payload;
  };

  const addService = async () => {
    try {
      setPostLoading(true);
      await request({
        url: "services",
        method: "POST",
        data: getPayload(),
      });
      toast.success("Service added successfully");
      navigate("/services-list");
    } catch (error) {
      makeError(error, toast);
    } finally {
      setPostLoading(false);
    }
  };

  const updateService = async () => {
    try {
      setPostLoading(true);
      await request({
        url: `services/${id}`,
        method: "POST",
        data: getPayload(),
      });
      toast.success("Service updated successfully");
      navigate("/services-list");
    } catch (error) {
      makeError(error, toast);
    } finally {
      setPostLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  return (
    <>
      <ToastContainer />

      <div className="container-fluid px-4 py-3">
        <ol className="breadcrumb bg-transparent mb-3">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item">Services</li>
          <li className="breadcrumb-item active">
            {id ? "Update" : "Add"} Service
          </li>
        </ol>

        {loading ? (
          <div className="text-center">Loading...</div>
        ) : !dataFound ? (
          <NotFoundTitle message="Service not found" />
        ) : (
          <div className="card">
            <div className="card-header">
              <h6>{id ? "Update Service" : "Add New Service"}</h6>
            </div>

            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Service Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    className="form-control"
                    value={formData.title}
                    onChange={handleInputChange}
                  />
                  {errors.title && (
                    <div className="text-danger">{errors.title}</div>
                  )}
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Price *</label>
                  <input
                    type="number"
                    name="price"
                    className="form-control"
                    value={formData.price}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Quantity</label>
                  <input
                    type="number"
                    name="qty"
                    className="form-control"
                    value={formData.qty}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Timeline *
                  </label>
                  <input
                    type="text"
                    name="timeline"
                    className="form-control"
                    placeholder="e.g. 3–5 business days"
                    value={formData.timeline}
                    onChange={handleInputChange}
                  />
                  {errors.timeline && (
                    <div className="text-danger">{errors.timeline}</div>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">
                    Service Description *
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: value,
                      }))
                    }
                    modules={quillModules}
                  />
                  {errors.description && (
                    <div className="text-danger mt-1">
                      {errors.description}
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">
                    Service Image
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaImage />
                    </span>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </div>

                  {imagePreview && (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="mt-3 rounded border"
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="card-footer text-end">
              <button
                className="btn btn-secondary"
                onClick={() => navigate("/services-list")}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary ms-3"
                onClick={submit}
                disabled={postLoading}
              >
                {postLoading
                  ? "Saving..."
                  : id
                  ? "Update Service"
                  : "Add Service"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AddServices;
